import React, { Suspense, lazy, useEffect, useState } from "react";
import { Mail, PenLine } from "lucide-react";
import { AppButton, AppModal, Callout, Checkbox, OtpInput, StepRail } from "../ui";
import Shimmer from "../Common/Shimmer";
import SignatureCapture from "../Students/profile/SignatureCapture";

// react-pdf is heavy and only needed once the student opens the agreement.
const PdfView = lazy(() => import("../Common/PdfView"));

const STEPS = [
  { key: "read", title: "Read", hint: "The evaluation and the terms" },
  { key: "verify", title: "Verify", hint: "A code sent to your email" },
  { key: "sign", title: "Sign", hint: "Your signature" },
];

const problem = (err, fallback) => err?.response?.data?.message || fallback;

/**
 * AgreementSignModal — the student acknowledges their placement agreement:
 * reads the PDF, proves their email with a one-time code, signs, submits.
 * `api` is the profile api's `agreement` half (profileApi.js) — the signed-in
 * door or the emailed link, the modal cannot tell. `onSigned(profile)` with
 * the fresh profile the server answers.
 */
const AgreementSignModal = ({ show, onHide, api, onSigned }) => {
  const [step, setStep] = useState(0);
  const [bytes, setBytes] = useState(null);
  const [pdfError, setPdfError] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [sent, setSent] = useState(null); // { maskedEmail, resendAfterSeconds }
  const [code, setCode] = useState("");
  const [token, setToken] = useState("");
  const [codeError, setCodeError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [signature, setSignature] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show) return undefined;
    setStep(0); setAgreed(false); setSent(null); setCode(""); setToken(""); setCodeError(""); setSignature(null); setError(""); setPdfError("");
    let live = true;
    api.pdfBytes()
      .then((b) => { if (live) setBytes(b); })
      .catch(() => { if (live) setPdfError("The agreement could not be loaded. Close this and try again."); });
    return () => { live = false; };
  }, [show, api]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const sendCode = async () => {
    setBusy("otp");
    setCodeError("");
    try {
      const data = await api.sendOtp();
      setSent(data);
      setCooldown(data.resendAfterSeconds || 60);
    } catch (err) {
      setCodeError(problem(err, "The code could not be sent."));
      if (err?.response?.data?.retryAfterSeconds) setCooldown(err.response.data.retryAfterSeconds);
    } finally {
      setBusy("");
    }
  };

  const verify = async (value = code) => {
    if (value.length < 6) return;
    setBusy("verify");
    setCodeError("");
    try {
      const data = await api.verifyOtp(value);
      setToken(data.token);
      setStep(2);
    } catch (err) {
      setCodeError(problem(err, "That code is not right."));
    } finally {
      setBusy("");
    }
  };

  const submit = async () => {
    setBusy("sign");
    setError("");
    try {
      const data = await api.sign({ otpToken: token, signature });
      onSigned?.(data);
    } catch (err) {
      setError(problem(err, "The signature could not be submitted."));
      if (err?.response?.data?.code === "OTP_REQUIRED") { setToken(""); setStep(1); }
    } finally {
      setBusy("");
    }
  };

  const footer = step === 0 ? (
    <>
      <AppButton variant="outline" onClick={onHide}>Not now</AppButton>
      <AppButton onClick={() => setStep(1)} disabled={!agreed || !bytes}>Continue</AppButton>
    </>
  ) : step === 1 ? (
    <>
      <AppButton variant="outline" onClick={() => setStep(0)}>Back</AppButton>
      <AppButton onClick={() => verify()} disabled={!sent || code.length < 6 || busy === "verify"}>{busy === "verify" ? "Checking…" : "Verify code"}</AppButton>
    </>
  ) : (
    <>
      <AppButton variant="outline" onClick={() => setStep(1)} disabled={busy === "sign"}>Back</AppButton>
      <AppButton onClick={submit} disabled={!signature || busy === "sign"}><PenLine size={16} /> {busy === "sign" ? "Submitting…" : "Acknowledge and sign"}</AppButton>
    </>
  );

  return (
    <AppModal show={show} onHide={busy ? undefined : onHide} title="Acknowledge your placement agreement" size="lg" footer={footer} closeOnOverlay={false}>
      <div className="tw-flex tw-flex-col tw-gap-4">
        <StepRail steps={STEPS} current={step} reached={step} variant="bar" />

        {step === 0 && (
          <>
            <Suspense fallback={<Shimmer height={480} />}>
              <PdfView bytes={bytes} label="Placement agreement" error={pdfError} />
            </Suspense>
            <Checkbox
              label="I have read the evaluation and the agreement, and I agree to its terms."
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
          </>
        )}

        {step === 1 && (
          <div className="tw-flex tw-flex-col tw-gap-4">
            <Callout tone="info">To confirm it is you, we send a six-digit code to the email address on your student record.</Callout>
            {!sent ? (
              <AppButton onClick={sendCode} disabled={busy === "otp" || cooldown > 0} className="tw-self-start">
                <Mail size={16} /> {busy === "otp" ? "Sending…" : cooldown > 0 ? `Try again in ${cooldown}s` : "Send the code"}
              </AppButton>
            ) : (
              <>
                <p className="tw-m-0 tw-text-sm tw-text-content">Code sent to <strong>{sent.maskedEmail}</strong>. It expires in {sent.expiresInMinutes} minutes.</p>
                <OtpInput value={code} onChange={setCode} onComplete={verify} invalid={!!codeError} autoFocus label="Verification code" />
                <AppButton variant="ghost" size="sm" onClick={sendCode} disabled={busy === "otp" || cooldown > 0} className="tw-self-start">
                  {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend the code"}
                </AppButton>
              </>
            )}
            {codeError && <Callout tone="danger" role="alert">{codeError}</Callout>}
          </div>
        )}

        {step === 2 && (
          <div className="tw-flex tw-flex-col tw-gap-4">
            <Callout tone="info">Your signature goes on the agreement with the date and the email address you verified. The institute signs after you.</Callout>
            <SignatureCapture onChange={setSignature} disabled={busy === "sign"} />
            {error && <Callout tone="danger" role="alert">{error}</Callout>}
          </div>
        )}
      </div>
    </AppModal>
  );
};

export default AgreementSignModal;
